<template>
    <div class="signature-canvas-wrapper">
        <div class="signature-type-toggle mb-3" v-if="allowTyped">
            <b-button-group size="sm">
                <b-button
                    :variant="signatureType === 'drawn' ? 'primary' : 'outline-primary'"
                    @click="setSignatureType('drawn')"
                >
                    <i class="fas fa-pen mr-1"></i>Draw
                </b-button>
                <b-button
                    :variant="signatureType === 'typed' ? 'primary' : 'outline-primary'"
                    @click="setSignatureType('typed')"
                >
                    <i class="fas fa-keyboard mr-1"></i>Type
                </b-button>
            </b-button-group>
        </div>

        <!-- Drawn Signature -->
        <div v-show="signatureType === 'drawn'" class="signature-pad-container">
            <div class="signature-pad-border" :class="{ 'disabled': disabled }">
                <canvas
                    ref="signatureCanvas"
                    :width="width"
                    :height="height"
                    class="signature-canvas"
                ></canvas>
                <div class="signature-line"></div>
                <span class="signature-label">Sign above this line</span>
            </div>
        </div>

        <!-- Typed Signature -->
        <div v-show="signatureType === 'typed'" class="typed-signature-container">
            <b-form-input
                v-model="typedName"
                :disabled="disabled"
                placeholder="Type your full legal name"
                class="typed-signature-input"
                @input="onTypedInput"
            />
            <div class="typed-signature-preview" v-if="typedName">
                <span class="signature-text">{{ typedName }}</span>
            </div>
        </div>

        <!-- Status indicator -->
        <div class="signature-status mt-2">
            <span v-if="hasSignature" class="text-success">
                <i class="fas fa-check-circle mr-1"></i>
                {{ signatureType === 'drawn' ? 'Signature captured' : 'Name entered' }}
            </span>
            <span v-else class="text-muted">
                <i class="fas fa-info-circle mr-1"></i>
                {{ signatureType === 'drawn' ? 'Please sign in the box above' : 'Please type your full legal name' }}
            </span>
        </div>
    </div>
</template>

<script>
import SignaturePad from 'signature_pad';

export default {
    name: 'SignatureCanvas',
    props: {
        width: {
            type: Number,
            default: 500
        },
        height: {
            type: Number,
            default: 200
        },
        penColor: {
            type: String,
            default: '#0d47a1' // Blue ink
        },
        disabled: {
            type: Boolean,
            default: false
        },
        allowTyped: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            signaturePad: null,
            signatureType: 'drawn',
            typedName: '',
            strokeData: [],
            currentStroke: null,
            strokeStartTime: null,
            isDrawing: false
        };
    },
    computed: {
        hasSignature() {
            if (this.signatureType === 'drawn') {
                return this.strokeData.length > 0;
            } else {
                return this.typedName.trim().length >= 2;
            }
        }
    },
    watch: {
        hasSignature(newVal) {
            this.$emit('signature-changed', newVal);
        },
        disabled(newVal) {
            if (this.signaturePad) {
                if (newVal) {
                    this.signaturePad.off();
                } else {
                    this.signaturePad.on();
                }
            }
        }
    },
    mounted() {
        this.initSignaturePad();
    },
    beforeDestroy() {
        if (this.signaturePad) {
            this.signaturePad.off();
        }
    },
    methods: {
        initSignaturePad() {
            const canvas = this.$refs.signatureCanvas;
            if (!canvas) return;

            this.signaturePad = new SignaturePad(canvas, {
                penColor: this.penColor,
                backgroundColor: 'rgba(255, 255, 255, 0)'
            });

            // Add event listeners for stroke tracking
            this.signaturePad.addEventListener('beginStroke', this.onBeginStroke);
            this.signaturePad.addEventListener('endStroke', this.onEndStroke);

            if (this.disabled) {
                this.signaturePad.off();
            }
        },

        setSignatureType(type) {
            this.signatureType = type;
            this.$emit('type-changed', type);
        },

        onBeginStroke() {
            if (this.disabled) return;

            this.isDrawing = true;
            this.strokeStartTime = Date.now();
            this.currentStroke = {
                color: this.penColor,
                startTime: this.strokeStartTime,
                points: []
            };

            // Only emit signature-started on first stroke
            if (this.strokeData.length === 0) {
                this.$emit('signature-started');
            }
        },

        onEndStroke() {
            if (this.disabled || !this.currentStroke) return;

            this.isDrawing = false;

            // Get the signature data from the pad
            const data = this.signaturePad.toData();

            // The last stroke is the current one - extract points with timing
            if (data && data.length > 0) {
                const lastGroup = data[data.length - 1];
                if (lastGroup && lastGroup.points) {
                    this.currentStroke.points = lastGroup.points.map((point, index) => ({
                        x: point.x,
                        y: point.y,
                        time: point.time || (this.strokeStartTime + (index * 10)),
                        pressure: point.pressure || 0.5
                    }));
                }
            }

            this.currentStroke.endTime = Date.now();
            this.strokeData.push(this.currentStroke);
            this.currentStroke = null;

            this.$emit('stroke-completed', this.strokeData.length);
        },

        onTypedInput() {
            this.$emit('signature-changed', this.hasSignature);
        },

        clear() {
            if (this.signatureType === 'drawn') {
                if (this.signaturePad) {
                    this.signaturePad.clear();
                }
                this.strokeData = [];
            } else {
                this.typedName = '';
            }
            this.$emit('signature-cleared');
            this.$emit('signature-changed', false);
        },

        isEmpty() {
            if (this.signatureType === 'drawn') {
                return this.signaturePad ? this.signaturePad.isEmpty() : true;
            } else {
                return this.typedName.trim().length < 2;
            }
        },

        getSignatureData() {
            if (this.signatureType === 'drawn') {
                if (!this.signaturePad || this.signaturePad.isEmpty()) {
                    return null;
                }

                // Get PNG data URL
                const imageDataUrl = this.signaturePad.toDataURL('image/png');

                return {
                    type: 'drawn',
                    image: imageDataUrl,
                    strokeData: this.strokeData,
                    typedValue: null,
                    capturedAt: new Date().toISOString()
                };
            } else {
                if (this.typedName.trim().length < 2) {
                    return null;
                }

                // Generate image from typed name using canvas
                const canvas = document.createElement('canvas');
                canvas.width = this.width;
                canvas.height = this.height;
                const ctx = canvas.getContext('2d');

                // Transparent background (canvas is transparent by default)
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                // Signature text styling
                ctx.font = 'italic 48px "Brush Script MT", "Segoe Script", cursive';
                ctx.fillStyle = this.penColor;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                ctx.fillText(this.typedName, 0, canvas.height * 0.7);

                const imageDataUrl = canvas.toDataURL('image/png');

                return {
                    type: 'typed',
                    image: imageDataUrl,
                    strokeData: [],
                    typedValue: this.typedName,
                    capturedAt: new Date().toISOString()
                };
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.signature-canvas-wrapper {
    .signature-type-toggle {
        .btn {
            min-width: 80px;
        }
    }

    .signature-pad-container {
        .signature-pad-border {
            position: relative;
            border: 2px solid #dee2e6;
            border-radius: 8px;
            background: #fff;
            overflow: hidden;
            display: inline-block;

            &.disabled {
                opacity: 0.6;
                pointer-events: none;
            }

            .signature-canvas {
                display: block;
                touch-action: none;
            }

            .signature-line {
                position: absolute;
                bottom: 50px;
                left: 20px;
                right: 20px;
                height: 1px;
                background: #ccc;
                pointer-events: none;
            }

            .signature-label {
                position: absolute;
                bottom: 30px;
                left: 0;
                right: 0;
                text-align: center;
                font-size: 12px;
                color: #999;
                pointer-events: none;
            }
        }
    }

    .typed-signature-container {
        .typed-signature-input {
            font-size: 1.1rem;
            padding: 12px 15px;
            border: 2px solid #dee2e6;
            border-radius: 8px;

            &:focus {
                border-color: #464577;
                box-shadow: 0 0 0 0.2rem rgba(70, 69, 119, 0.25);
            }
        }

        .typed-signature-preview {
            margin-top: 15px;
            padding: 30px 20px;
            background: #fff;
            border: 2px solid #dee2e6;
            border-radius: 8px;
            text-align: center;
            min-height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;

            .signature-text {
                font-family: 'Brush Script MT', 'Segoe Script', cursive;
                font-size: 48px;
                font-style: italic;
                color: #000;
            }
        }
    }

    .signature-status {
        font-size: 0.9rem;
    }
}

// Responsive adjustments
@media (max-width: 576px) {
    .signature-canvas-wrapper {
        .typed-signature-preview .signature-text {
            font-size: 32px;
        }
    }
}
</style>
